import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageShell } from '../components/PageShell';
import { useAuth } from '../context/AuthContext';
import { formatApiError, orderApi } from '../lib/orderApi';

const inputStyle = {
  width: '100%', background: '#141414', border: '1px solid rgba(201,168,76,0.25)',
  color: '#F5F5F0', padding: '13px 15px', fontSize: '14px', outline: 'none',
};

const goldButton = {
  background: '#C9A84C', color: '#0A0A0A', fontWeight: 600,
  padding: '12px 20px', border: 'none', cursor: 'pointer', width: '100%',
};

// Only allow internal relative paths (defense-in-depth against a tampered
// 'yamauchi_auth_next' localStorage value being passed to navigate()).
function getSafeNextPath(next) {
  if (typeof next === 'string' && /^\/(?!\/)[^\s]*$/.test(next)) {
    return next;
  }
  return '/pedir';
}

const AuthCallback = () => {
  const { googleLogin, setToken } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [err, setErr] = useState('');
  
  // 2FA state for admin Google OAuth
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tfaSessionId, setTfaSessionId] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [nextPath, setNextPath] = useState('/pedir');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    const next = getSafeNextPath(localStorage.getItem('yamauchi_auth_next'));
    setNextPath(next);
    
    if (!sessionId) {
      navigate('/login');
      return;
    }
    googleLogin(sessionId)
      .then((result) => {
        localStorage.removeItem('yamauchi_auth_next');
        // Check if 2FA is required (admin via Google OAuth)
        if (result && result.requires_2fa) {
          setNeeds2FA(true);
          setTfaSessionId(result.session_id);
        } else {
          navigate(next, { replace: true });
        }
      })
      .catch(() => setErr('No se pudo iniciar sesión con Google. Inténtalo de nuevo.'));
  }, [googleLogin, navigate]);

  const verify2FA = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await orderApi.post('/auth/verify-2fa', {
        session_id: tfaSessionId,
        code: tfaCode.trim(),
      });
      localStorage.removeItem('yamauchi_auth_next');
      await setToken();
      navigate(nextPath, { replace: true });
    } catch (error) {
      setErr(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setBusy(false);
    }
  };

  const resend2FA = async () => {
    setBusy(true);
    setErr('');
    try {
      await orderApi.post('/auth/resend-2fa', { session_id: tfaSessionId });
      setErr('✓ Código reenviado');
      setTimeout(() => setErr(''), 3000);
    } catch (error) {
      setErr(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setBusy(false);
    }
  };

  // Show 2FA form if required for admin
  if (needs2FA) {
    return (
      <PageShell>
        <div className="text-center" style={{ paddingTop: '40px', maxWidth: '400px', margin: '0 auto' }}>
          <h2 className="font-display" style={{ fontSize: '24px', color: '#C9A84C', marginBottom: '16px' }}>
            Verificación de seguridad
          </h2>
          <p className="font-body" style={{ color: '#C2C1B6', marginBottom: '24px' }}>
            Ingresa el código de 6 dígitos enviado a tu email
          </p>
          <form onSubmit={verify2FA}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="Código de 6 dígitos"
              value={tfaCode}
              onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ ...inputStyle, textAlign: 'center', letterSpacing: '8px', fontSize: '20px', marginBottom: '16px' }}
              autoFocus
              data-testid="2fa-code-input"
            />
            <button type="submit" style={goldButton} disabled={busy || tfaCode.length !== 6} data-testid="2fa-submit-btn">
              {busy ? 'Verificando...' : 'Verificar'}
            </button>
          </form>
          <button
            onClick={resend2FA}
            disabled={busy}
            style={{ background: 'none', border: 'none', color: '#C9A84C', marginTop: '16px', cursor: 'pointer' }}
            data-testid="resend-2fa-btn"
          >
            Reenviar código
          </button>
          {err && (
            <p className="font-body mt-3" style={{ color: err.startsWith('✓') ? '#4ade80' : '#E0736F' }}>
              {err}
            </p>
          )}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="text-center" style={{ paddingTop: '40px' }}>
        <p className="font-display" style={{ fontSize: '24px' }}>Iniciando sesión…</p>
        {err && <p className="font-body mt-3" style={{ color: '#E0736F' }}>{err}</p>}
      </div>
    </PageShell>
  );
};

export default AuthCallback;
